import React from 'react';

import classNames from 'classnames';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

export type AccountSectionId =
  | 'resumen'
  | 'usuarios'
  | 'telefono'
  | 'ruteo'
  | 'vivo'
  | 'reportes'
  | 'tabulaciones'
  | 'resoluciones';

export const ACCOUNT_SECTIONS: { id: AccountSectionId; label: string; icon: string }[] = [
  { id: 'resumen', label: 'Resumen', icon: OrbitIcons.Home },
  { id: 'usuarios', label: 'Usuarios', icon: OrbitIcons.UserPlus },
  { id: 'telefono', label: 'Teléfono', icon: OrbitIcons.Phone },
  { id: 'ruteo', label: 'Enrutamiento', icon: OrbitIcons.Workflow },
  { id: 'vivo', label: 'En vivo', icon: OrbitIcons.LayoutDashboard },
  { id: 'reportes', label: 'Reportes', icon: OrbitIcons.BarChart3 },
  { id: 'tabulaciones', label: 'Tabulaciones', icon: OrbitIcons.Hash },
  { id: 'resoluciones', label: 'Resoluciones', icon: OrbitIcons.Workflow },
];

interface AccountSidebarProps {
  accountName: string;
  phoneNumber?: string | null;
  activeSection: AccountSectionId;
  onSectionChange: (section: AccountSectionId) => void;
}

export const AccountSidebar: React.FC<AccountSidebarProps> = ({
  accountName,
  phoneNumber,
  activeSection,
  onSectionChange,
}) => {
  return (
    <aside className="flex h-full w-56 flex-shrink-0 flex-col border-r border-white-2.0 bg-white-0.0">
      {/* Account info header */}
      <div className="border-b border-white-2.0 px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-0.0">
            <Icon name={OrbitIcons.Building} size={20} className="text-primary-2.0" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-bold text-black-1.0" title={accountName}>
              {accountName}
            </p>
            {phoneNumber && (
              <p className="truncate font-mono text-xs text-gray-2.0">{phoneNumber}</p>
            )}
          </div>
        </div>
      </div>

      {/* Navigation sections */}
      <nav className="flex-1 py-2 overflow-y-auto">
        <ul className="space-y-0.5 px-2">
          {ACCOUNT_SECTIONS.map((section, idx) => {
            const isActive = activeSection === section.id;
            return (
              <li key={section.id}>
                <button
                  type="button"
                  onClick={() => onSectionChange(section.id)}
                  className={classNames(
                    'group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all',
                    isActive
                      ? 'bg-primary-2.0 text-white-0.0 shadow-sm'
                      : 'text-gray-3.0 hover:bg-white-1.0 hover:text-black-1.0'
                  )}
                  title={`${section.label} (${idx + 1})`}
                >
                  <div
                    className={classNames(
                      'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md transition-colors',
                      isActive ? 'bg-white/20' : 'bg-white-1.0 group-hover:bg-white-2.0'
                    )}
                  >
                    <Icon
                      name={section.icon}
                      size={16}
                      className={classNames(
                        'transition-colors',
                        isActive ? 'text-white-0.0' : 'text-gray-2.0 group-hover:text-primary-2.0'
                      )}
                    />
                  </div>
                  <span
                    className={classNames(
                      'flex-1 text-sm font-medium transition-colors',
                      isActive ? 'font-semibold' : ''
                    )}
                  >
                    {section.label}
                  </span>
                  <span
                    className={classNames(
                      'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-[10px] font-bold',
                      isActive
                        ? 'bg-white/20 text-white-0.0'
                        : 'bg-white-1.0 text-gray-2.0 group-hover:bg-white-2.0'
                    )}
                  >
                    {idx + 1}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer hint */}
      <div className="border-t border-white-2.0 px-4 py-3">
        <p className="text-center text-[10px] text-gray-2.0">
          Usa las teclas <kbd className="rounded bg-white-1.0 px-1 font-mono">1</kbd>-
          <kbd className="rounded bg-white-1.0 px-1 font-mono">8</kbd> para navegar
        </p>
      </div>
    </aside>
  );
};
